import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FilePlus2, UserPlus } from "lucide-react";
import { AdminAppShell } from "../../design-system/shells/AdminAppShell";
import { useAuth } from "../../lib/auth";
import { api } from "../../lib/api";
import { errMsg } from "../../lib/portfolio";
import { ARCH_DISCLAIMER, REVIEW_LABELS } from "../../lib/phaseFeatures";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent } from "../../components/ui/card";
import { ArchDisclaimer } from "./ArchitectGate";
import { RfiPanel } from "./RfiPanel";
import { SubmittalPanel } from "./SubmittalPanel";
import { SchedulePanel } from "./SchedulePanel";
import { ProcurementPanel } from "./ProcurementPanel";
import { ChangeOrderPanel } from "./ChangeOrderPanel";

const DISCIPLINES = ["architectural", "structural", "mep", "civil", "landscape", "interior", "other"];

function NewDocumentForm({ pid, onCreated }) {
  const [title, setTitle] = useState("");
  const [discipline, setDiscipline] = useState("architectural");
  const [sheet, setSheet] = useState("");
  const [error, setError] = useState(null);
  const submit = (e) => {
    e.preventDefault();
    api.post(`/phase3/architect/projects/${pid}/documents`, { title, discipline, sheet_number: sheet })
      .then(() => { setTitle(""); setSheet(""); setError(null); onCreated(); })
      .catch((err) => setError(errMsg(err, "Could not create the drawing.")));
  };
  return (
    <form onSubmit={submit} className="grid gap-3 md:grid-cols-[2fr_1fr_1fr_auto] items-end" data-testid="phase3-new-doc-form">
      <label className="text-sm font-nav text-erf-dark">Drawing title
        <Input required value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1 h-11" data-testid="phase3-new-doc-title" />
      </label>
      <label className="text-sm font-nav text-erf-dark">Discipline
        <select value={discipline} onChange={(e) => setDiscipline(e.target.value)} data-testid="phase3-new-doc-discipline"
          className="mt-1 h-11 w-full rounded-md border border-erf-borderCream bg-white px-3 text-sm">
          {DISCIPLINES.map((d) => <option key={d} value={d}>{d.toUpperCase() === "MEP" ? "MEP" : d[0].toUpperCase() + d.slice(1)}</option>)}
        </select>
      </label>
      <label className="text-sm font-nav text-erf-dark">Sheet no.
        <Input value={sheet} onChange={(e) => setSheet(e.target.value)} placeholder="A-101" className="mt-1 h-11" data-testid="phase3-new-doc-sheet" />
      </label>
      <Button type="submit" className="h-11 bg-erf-gold text-erf-dark hover:bg-erf-goldHover" data-testid="phase3-new-doc-submit">
        <FilePlus2 className="w-4 h-4 mr-1.5" aria-hidden="true" />Add drawing
      </Button>
      {error && <p role="alert" className="text-sm text-[#991B1B] md:col-span-4" data-testid="phase3-new-doc-error">{error}</p>}
    </form>
  );
}

function ArchitectsPanel({ pid }) {
  const [rows, setRows] = useState([]);
  const [email, setEmail] = useState("");
  const [error, setError] = useState(null);
  const load = useCallback(() => api.get(`/phase3/architect/projects/${pid}/architects`).then((r) => setRows(r.data)), [pid]);
  useEffect(() => { load(); }, [load]);
  const assign = (e) => {
    e.preventDefault();
    api.post(`/phase3/architect/projects/${pid}/architects`, { email })
      .then(() => { setEmail(""); setError(null); load(); })
      .catch((err) => setError(errMsg(err, "Could not assign the architect.")));
  };
  const revoke = (id) => api.delete(`/phase3/architect/projects/${pid}/architects/${id}`).then(load);
  return (
    <Card className="ds-card border-erf-borderCream mb-5" data-testid="phase3-architects-panel">
      <CardContent className="p-5">
        <h2 className="font-display font-semibold text-erf-dark text-base md:text-lg">Architects on this project</h2>
        <ul className="mt-2 space-y-2">
          {rows.length === 0 && <li className="text-sm font-nav text-erf-dark/80">No architects assigned yet.</li>}
          {rows.map((r) => (
            <li key={r.id} className="flex flex-wrap items-center gap-2 text-sm font-nav text-erf-dark" data-testid={`phase3-architect-${r.id}`}>
              <span className="mr-auto">{r.name} · {r.email}</span>
              <Button variant="outline" className="h-11 border-erf-borderCream" onClick={() => revoke(r.id)} data-testid={`phase3-architect-revoke-${r.id}`}>Remove</Button>
            </li>
          ))}
        </ul>
        <form onSubmit={assign} className="mt-3 flex flex-wrap gap-2 items-end">
          <label className="text-sm font-nav text-erf-dark flex-1 min-w-[220px]">Architect's email
            <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 h-11" data-testid="phase3-assign-email" />
          </label>
          <Button type="submit" className="h-11 bg-erf-dark text-[#FEFCF7]" data-testid="phase3-assign-submit">
            <UserPlus className="w-4 h-4 mr-1.5" aria-hidden="true" />Assign
          </Button>
        </form>
        {error && <p role="alert" className="mt-2 text-sm text-[#991B1B]" data-testid="phase3-assign-error">{error}</p>}
      </CardContent>
    </Card>
  );
}

export default function ArchitectWorkspace() {
  const { user } = useAuth();
  const [projects, setProjects] = useState(null);
  const [pid, setPid] = useState(null);
  const [docs, setDocs] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/phase3/architect/projects")
      .then((r) => { setProjects(r.data); setPid((p) => p || r.data.items[0]?.id || null); })
      .catch((e) => setError(errMsg(e, "Could not load projects.")));
  }, []);
  const loadDocs = useCallback(() => {
    if (pid) api.get(`/phase3/architect/projects/${pid}/documents`).then((r) => setDocs(r.data));
  }, [pid]);
  useEffect(() => { loadDocs(); }, [loadDocs]);

  const manager = projects?.role === "manager";
  return (
    <AdminAppShell activeNav="architect" title="Architect Workspace" user={user}
      subtitle="Drawings, numbered revisions and design review for each project."
      breadcrumbs={[{ label: "Owner Command Center", to: "/owner" }, { label: "Architect Workspace", to: null }]}>
      <div data-testid="phase3-workspace">
        <ArchDisclaimer text={projects?.disclaimer || ARCH_DISCLAIMER} />
        {error && <p role="alert" className="text-sm text-[#991B1B]" data-testid="phase3-workspace-error">{error}</p>}
        {projects && projects.items.length === 0 && (
          <Card className="ds-card border-erf-borderCream" data-testid="phase3-no-projects">
            <CardContent className="p-6">
              <h2 className="font-display font-semibold text-erf-dark text-base md:text-lg">No projects yet.</h2>
              <p className="text-sm font-nav text-erf-dark/80 mt-1">
                {manager ? "Projects appear here once they exist in the project record." : "Projects you are assigned to will appear here."}
              </p>
            </CardContent>
          </Card>
        )}
        {projects && projects.items.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-5" role="group" aria-label="Choose a project" data-testid="phase3-project-picker">
            {projects.items.map((p) => (
              <Button key={p.id} variant={pid === p.id ? "default" : "outline"} aria-pressed={pid === p.id}
                className={pid === p.id ? "h-11 bg-erf-dark text-[#FEFCF7]" : "h-11 border-erf-borderCream text-erf-dark bg-white"}
                onClick={() => setPid(p.id)} data-testid={`phase3-project-${p.id}`}>
                {p.name} · {p.document_count}
              </Button>
            ))}
          </div>
        )}
        {pid && manager && <ArchitectsPanel pid={pid} />}
        {pid && manager && (
          <Card className="ds-card border-erf-borderCream mb-5">
            <CardContent className="p-5"><NewDocumentForm pid={pid} onCreated={loadDocs} /></CardContent>
          </Card>
        )}
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" data-testid="phase3-doc-grid">
          {(docs?.items || []).map((d) => (
            <Link key={d.id} to={`/phase3/architect/documents/${d.id}`} data-testid={`phase3-doc-card-${d.id}`}
              className="ds-card block rounded-xl border border-erf-borderCream bg-[#FEFCF7] p-4 transition-shadow hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-erf-gold">
              <div className="ds-card-header">
                <div className="ds-card-header__title">
                  <div className="font-display font-semibold text-erf-dark">{d.sheet_number ? `${d.sheet_number} · ` : ""}{d.title}</div>
                  <div className="text-xs font-nav text-erf-dark/80 ds-text-safe">
                    Status {d.status_letter} — {d.status_name} · v{d.current_version}
                  </div>
                </div>
              </div>
              <p className="mt-2 text-sm font-nav text-erf-dark/80">{REVIEW_LABELS[d.review_state] || d.review_state}</p>
            </Link>
          ))}
        </div>
        {pid && <RfiPanel pid={pid} manager={manager} />}
        {pid && <SubmittalPanel key={pid} pid={pid} manager={manager} />}
        {pid && manager && <ChangeOrderPanel pid={pid} />}
        {pid && <SchedulePanel key={`s-${pid}`} pid={pid} manager={manager} />}
        {pid && manager && <ProcurementPanel key={`p-${pid}`} pid={pid} />}
      </div>
    </AdminAppShell>
  );
}
